import { apiClient } from './api';
import { getSOWInsightsForPrepare } from './aiService';

// Get full prepare phase data for a project
export const getPreparePhaseData = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase`);
    return response.data;
  } catch (error) {
    console.error('Error fetching prepare phase data:', error);
    throw error;
  }
};

// Project Scope
export const getProjectScope = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/project-scope`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching project scope:', error);
    return null;
  }
};

export const saveProjectScope = async (projectId, scopeData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/project-scope`, scopeData);
    return response.data;
  } catch (error) {
    console.error('Error saving project scope:', error);
    throw error;
  }
};

// Business Requirements
export const getBusinessRequirements = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/business-requirements`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching business requirements:', error);
    return null;
  }
};

export const saveBusinessRequirements = async (projectId, requirementsData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/business-requirements`, requirementsData);
    return response.data;
  } catch (error) {
    console.error('Error saving business requirements:', error);
    throw error;
  }
};

// Integration Map
export const getIntegrationMap = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/integration-map`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching integration map:', error);
    return null;
  }
};

export const saveIntegrationMap = async (projectId, integrationData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/integration-map`, integrationData);
    return response.data;
  } catch (error) {
    console.error('Error saving integration map:', error);
    throw error;
  }
};

// Project Team
export const getStakeholderAnalysis = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/stakeholder-analysis`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching stakeholder analysis:', error);
    return null;
  }
};

export const saveStakeholderAnalysis = async (projectId, stakeholderData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/stakeholder-analysis`, stakeholderData);
    return response.data;
  } catch (error) {
    console.error('Error saving stakeholder analysis:', error);
    throw error;
  }
};

// Project Timeline
export const getProjectTimeline = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/project-timeline`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching project timeline:', error);
    return null;
  }
};

export const saveProjectTimeline = async (projectId, timelineData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/project-timeline`, timelineData);
    return response.data;
  } catch (error) {
    console.error('Error saving project timeline:', error);
    throw error;
  }
};

export const fetchTimelineFromSOW = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/project-timeline/fetch-from-sow`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project timeline from project documents:', error);
    throw error;
  }
};

// Workshop Schedule
export const getWorkshopSchedule = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/workshop-schedule`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching workshop schedule:', error);
    return null;
  }
};

export const saveWorkshopSchedule = async (projectId, scheduleData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/workshop-schedule`, scheduleData);
    return response.data;
  } catch (error) {
    console.error('Error saving workshop schedule:', error);
    throw error;
  }
};

// Helper function to load data with DB first, then AI fallback
export const loadPreparePhaseData = async (projectId, section) => {
  // First try to get data from database
  let data = null;
  
  switch(section) {
    case 'project_scope':
      data = await getProjectScope(projectId);
      break;
    case 'business_requirements':
      data = await getBusinessRequirements(projectId);
      break;
    case 'integration_map':
      data = await getIntegrationMap(projectId);
      break;
    case 'stakeholder_analysis':
      data = await getStakeholderAnalysis(projectId);
      break;
    case 'project_timeline':
      data = await getProjectTimeline(projectId);
      break;
    case 'workshop_schedule':
      data = await getWorkshopSchedule(projectId);
      break;
    case 'technical_requirements':
      data = await getTechnicalRequirements(projectId);
      break;
    default:
      // No recognized section
      break;
  }
  
  // If database data exists, return it
  if (data) {
    return data;
  }
  
  // Otherwise, fall back to AI generation
  try {
    return await getSOWInsightsForPrepare(projectId, section);
  } catch (error) {
    console.error(`Error getting ${section} data with AI fallback:`, error);
    throw error;
  }
};

// Generate fresh data using AI and save it to the database
export const generateWithAIAndSave = async (projectId, section) => {
  try {
    // Generate with AI
    const aiData = await getSOWInsightsForPrepare(projectId, section);
    
    if (!aiData) {
      throw new Error('AI generation failed');
    }
    
    // Save to database based on section
    let savedResponse;
    switch(section) {
      case 'project_scope':
        savedResponse = await saveProjectScope(projectId, aiData);
        break;
      case 'business_requirements':
        savedResponse = await saveBusinessRequirements(projectId, aiData);
        break;
      case 'integration_map':
        savedResponse = await saveIntegrationMap(projectId, aiData);
        break;
      case 'stakeholder_analysis':
        savedResponse = await saveStakeholderAnalysis(projectId, aiData);
        break;
      case 'project_timeline':
        savedResponse = await saveProjectTimeline(projectId, aiData);
        break;
      case 'workshop_schedule':
        savedResponse = await saveWorkshopSchedule(projectId, aiData);
        break;
      case 'technical_requirements':
        savedResponse = await saveTechnicalRequirements(projectId, aiData);
        break;
      default:
        throw new Error(`Unknown section: ${section}`);
    }
    
    return savedResponse.data || aiData;
  } catch (error) {
    console.error(`Error generating ${section} with AI and saving:`, error);
    throw error;
  }
};

// Fetch project scope from SOW using AI and save to database
export const fetchProjectScopeFromSOW = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/project-scope/fetch-from-sow`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project scope from SOW:', error);
    throw error;
  }
};

// Fetch business requirements from SOW using AI and save to database
export const fetchBusinessRequirementsFromSOW = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/business-requirements/fetch-from-sow`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business requirements from SOW:', error);
    throw error;
  }
};

// Technical Requirements
export const getTechnicalRequirements = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/prepare-phase/technical-requirements`);
    if (response.data && !response.data.message) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching technical requirements:', error);
    return null;
  }
};

export const saveTechnicalRequirements = async (projectId, techReqData) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/technical-requirements`, techReqData);
    return response.data;
  } catch (error) {
    console.error('Error saving technical requirements:', error);
    throw error;
  }
};

// Fetch technical requirements from SOW using AI and save to database
export const fetchTechnicalRequirementsFromSOW = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/technical-requirements/fetch-from-sow`);
    return response.data;
  } catch (error) {
    console.error('Error fetching technical requirements from SOW:', error);
    throw error;
  }
};

// Fetch workshop schedule from SOW using AI and save to database
export const fetchWorkshopScheduleFromSOW = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/prepare-phase/workshop-schedule/fetch-from-sow`);
    return response.data;
  } catch (error) {
    console.error('Error fetching workshop schedule from SOW:', error);
    throw error;
  }
};